/**
 * The composer's editor — TipTap in place of the textarea, so a mention can be
 * a chip. The old web's `MessageInput.tsx`, reduced to the editor itself:
 *
 *   - `value` is the **serialised marker string** (`documentToMarkerText`),
 *     which is what the send path and the draft hold; the editor is the source
 *     of truth while typing and only syncs *from* `value` when it is cleared.
 *     A non-empty initial value (a restored draft) is rebuilt into chips
 *     through the same HTML the paste path uses, or `parseHTML` never sees a
 *     mention and the markers show as text.
 *   - Enter sends, Shift+Enter is a newline — unless a suggestion is open,
 *     which is detected the reference's way: an `@` before the caret with no
 *     space after it. Enter while a turn is busy is a newline, as before.
 *   - Two clipboard serialisers with one payload (`@label`, `@label: p1|p2`):
 *     `renderText` for the ordinary path and `clipboardTextSerializer` for
 *     drag-drop and multi-block copies, which bypass it (the reference's #251).
 *   - A **drop target** for the Revit index (`REVIT_INDEX_MIME`): the
 *     ProseMirror `handleDrop` inserts the chip *at the drop point*
 *     (`posAtCoords`), while React drag handlers on the wrapper — with a depth
 *     counter, because enter/leave fire for every child — draw the ring and
 *     the "Drop to insert" pill. `getData` is unreadable during dragover, so
 *     the wrapper sniffs `dataTransfer.types`. A click on an index row inserts
 *     at the caret instead (`insertDocumentContextItem`).
 *
 * The mention extension is only installed when `mentionsEnabled` — the
 * reference gated it on the Revit plugin mode. Without it the editor is a
 * plain text box with the same keys. The `@` menu itself is gated by data:
 * with no bound instance the suggestion's `items()` returns nothing.
 */
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import type { JSONContent } from "@tiptap/core";
import type { MentionOptions } from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import { TextSelection } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type DragEvent, type MutableRefObject } from "react";
import { CustomMention } from "../../extensions/CustomMention";
import { createDocumentContextSuggestion, type DocumentContextSuggestionConfig } from "../../extensions/DocumentContextSuggestion";
import { useLocale } from "../../i18n/LocaleProvider";
import {
  REVIT_INDEX_MIME,
  isRevitIndexDrag,
  parseDocumentContextMentionSource,
  toDocumentContextMentionAttrs,
  type DocumentContextMentionSource,
} from "../../lib/documentContextMention";
import { documentToMarkerText, mentionNodeText, suggestionMayBeOpen, type TextNodeLike } from "../../lib/mentionEditorText";
import { buildMentionClipboardHtml } from "../../lib/mentionUtils";

export interface MentionEditorHandle {
  /** Replace the draft with editor content — a starter prompt with its mention node. */
  setContent: (content: JSONContent) => void;
  /** Plain text after whatever is in the editor, one paragraph per line. */
  appendText: (text: string) => void;
  /** A Revit index row, clicked: a chip at the caret. False when nothing can be inserted. */
  insertDocumentContextItem: (source: DocumentContextMentionSource) => boolean;
  focus: () => void;
  /**
   * The draft as it is in the editor right now, as marker text. Read at send rather
   * than the last rendered `value`: Enter submits on a timer, and a keystroke still
   * on its way to `value` would be cut off the message.
   */
  text: () => string;
  /**
   * Empty the editor now. A send clears it explicitly rather than relying on `value`
   * turning "" — that sync could be missed (the first message also swaps which
   * editor is mounted), and the sent words stayed in the box.
   */
  clear: () => void;
}

/** The document as marker text — the old `getProcessedText`. */
export function getProcessedText(editor: Editor): string {
  const nodes: TextNodeLike[] = [];
  editor.state.doc.descendants((node) => {
    nodes.push({ typeName: node.type.name, isText: node.isText, isBlock: node.isBlock, text: node.text, attrs: node.attrs });
    return true;
  });
  return documentToMarkerText(nodes);
}

/** Plain text as editor paragraphs, one per line; an empty line is an empty paragraph. */
function textParagraphs(text: string): JSONContent[] {
  return text.split("\n").map((line) => ({ type: "paragraph", content: line ? [{ type: "text", text: line }] : [] }));
}

export const MentionEditor = forwardRef<
  MentionEditorHandle,
  {
    value: string;
    onChange: (marker: string) => void;
    onSubmit: () => void;
    /** Enter is a newline while this is true (a turn is streaming). */
    submitBlocked: boolean;
    disabled: boolean;
    placeholder: string;
    mentionsEnabled: boolean;
    /** The document context can be browsed: index rows may be dropped or clicked in. */
    dropEnabled: boolean;
    suggestionConfig: MutableRefObject<DocumentContextSuggestionConfig>;
    /**
     * Files pasted into the editor — a screenshot, mostly — handed to the composer's
     * attachments, as the old web's `handlePaste` did. Null while attaching is not
     * allowed: the paste is then swallowed rather than written into the draft.
     */
    onPasteFiles: ((files: File[]) => void) | null;
  }
>(function MentionEditor({ value, onChange, onSubmit, submitBlocked, disabled, placeholder, mentionsEnabled, dropEnabled, suggestionConfig, onPasteFiles }, ref) {
  const { t } = useLocale();
  const onSubmitRef = useRef(onSubmit);
  const submitBlockedRef = useRef(submitBlocked);
  const placeholderRef = useRef(placeholder);
  const dropEnabledRef = useRef(dropEnabled);
  onSubmitRef.current = onSubmit;
  submitBlockedRef.current = submitBlocked;
  placeholderRef.current = placeholder;
  dropEnabledRef.current = dropEnabled;
  // Read through a ref: TipTap captures `editorProps` once, when the editor is made.
  const onPasteFilesRef = useRef(onPasteFiles);
  onPasteFilesRef.current = onPasteFiles;

  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        // A chat draft is one paragraph with soft breaks, not a document.
        bulletList: false,
        orderedList: false,
        link: false,
      }),
      Placeholder.configure({ placeholder: () => placeholderRef.current }),
      ...(mentionsEnabled
        ? [
            CustomMention.configure({
              HTMLAttributes: {},
              renderText: ({ node }) => mentionNodeText(node.attrs),
              // The stock extension types `command`'s `props` as its own node
              // attrs; ours are the menu's options, which `command` maps to
              // attrs itself. The shapes meet inside the suggestion, not here.
              suggestion: createDocumentContextSuggestion(suggestionConfig) as unknown as MentionOptions["suggestion"],
            }),
          ]
        : []),
    ],
    [mentionsEnabled, suggestionConfig],
  );

  const editor = useEditor(
    {
      extensions,
      // One paragraph per line: a multi-line draft seeded into a freshly mounted
      // editor (the docked composer replacing the centred one) keeps its breaks,
      // where one HTML string folded every newline into a space.
      content: mentionsEnabled
        ? value.split("\n").map((line) => `<p>${buildMentionClipboardHtml(line)}</p>`).join("")
        : { type: "doc", content: textParagraphs(value) },
      editable: !disabled,
      onUpdate: ({ editor: e }) => onChange(mentionsEnabled ? getProcessedText(e) : e.getText()),
      editorProps: {
        attributes: { class: "mention-editor-content" },
        clipboardTextSerializer: (slice) =>
          slice.content.textBetween(0, slice.content.size, "\n", (leaf) =>
            leaf.type.name === "mention" ? mentionNodeText(leaf.attrs) : (leaf.text ?? ""),
          ),
        handleKeyDown: (view, event) => {
          if (event.key !== "Enter" || event.shiftKey) return false;
          // The Enter that confirms an input-method candidate (注音, 倉頡, Japanese…)
          // is the IME's, not a send. ProseMirror skips keys while it knows a
          // composition is open; this covers the keydown that arrives flagged as
          // composing after it has been told the composition ended.
          if (event.isComposing || event.keyCode === 229 || view.composing) return false;
          if (submitBlockedRef.current) return false;
          const { $from } = view.state.selection;
          if (suggestionMayBeOpen(view.state.doc.textBetween(0, $from.pos))) return false;
          event.preventDefault();
          setTimeout(() => onSubmitRef.current(), 0);
          return true;
        },
        // The old `handlePaste`: files go to the attachments; text and HTML (chips
        // included) paste as before. Without this the editor drops the image silently.
        handlePaste: (_view, event) => {
          const files = event.clipboardData?.files;
          if (!files || files.length === 0) return false;
          event.preventDefault();
          onPasteFilesRef.current?.(Array.from(files));
          return true;
        },
        // The old `handleDrop`: a chip where the pointer let go, the caret after it.
        handleDrop: (view, event) => {
          const raw = event.dataTransfer?.getData(REVIT_INDEX_MIME);
          if (!raw) return false;
          event.preventDefault();
          if (!dropEnabledRef.current) return true;
          const item = parseDocumentContextMentionSource(raw);
          const mentionType = view.state.schema.nodes["mention"];
          const position = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
          if (!item || !mentionType || position === undefined) return true;
          const mention = mentionType.create(toDocumentContextMentionAttrs(item));
          const tr = view.state.tr.insert(position, mention).insertText(" ", position + mention.nodeSize);
          tr.setSelection(TextSelection.near(tr.doc.resolve(position + mention.nodeSize + 1)));
          view.dispatch(tr);
          view.focus();
          return true;
        },
      },
    },
    [extensions],
  );

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // The placeholder decoration only recomputes on a transaction; nudge one
  // when its text changes (blocked → ready) so the box does not keep saying
  // "sign in first" after the sign-in.
  useEffect(() => {
    if (editor) editor.view.dispatch(editor.state.tr);
  }, [editor, placeholder]);

  // Only a *cleared* value syncs into the editor (after a send); while typing
  // the editor is the source of truth and `value` follows it.
  const previous = useRef(value);
  useEffect(() => {
    if (!editor || value === previous.current) return;
    if (value === "" && !editor.isEmpty) editor.commands.clearContent(true);
    previous.current = value;
  }, [editor, value]);

  useImperativeHandle(
    ref,
    () => ({
      setContent: (content) => {
        if (!editor) return;
        editor.chain().focus("end").setContent(content, { emitUpdate: true }).run();
      },
      appendText: (text) => {
        editor?.chain().focus("end").insertContent(textParagraphs(text)).run();
      },
      insertDocumentContextItem: (source) => {
        if (!editor || !dropEnabledRef.current) return false;
        return editor
          .chain()
          .focus()
          .insertContent([
            { type: "mention", attrs: toDocumentContextMentionAttrs(source) },
            { type: "text", text: " " },
          ])
          .run();
      },
      focus: () => editor?.commands.focus(),
      text: () => (editor ? (mentionsEnabled ? getProcessedText(editor) : editor.getText()) : ""),
      clear: () => {
        editor?.commands.clearContent(true);
      },
    }),
    [editor, mentionsEnabled],
  );

  // --- the drag ring ------------------------------------------------------------
  const clearDrag = useCallback(() => {
    dragDepth.current = 0;
    setDragOver(false);
  }, []);

  useEffect(() => {
    window.addEventListener("dragend", clearDrag);
    return () => window.removeEventListener("dragend", clearDrag);
  }, [clearDrag]);

  useEffect(() => {
    if (!dropEnabled) clearDrag();
  }, [dropEnabled, clearDrag]);

  const isIndexDrag = (event: DragEvent<HTMLDivElement>) => dropEnabled && isRevitIndexDrag(Array.from(event.dataTransfer.types));

  return (
    <div
      className={`mention-editor-wrap${dragOver ? " is-drag-over" : ""}`}
      onDragEnter={(event) => {
        if (!isIndexDrag(event)) return;
        event.preventDefault();
        dragDepth.current += 1;
        setDragOver(true);
      }}
      onDragOver={(event) => {
        if (!isIndexDrag(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={(event) => {
        if (!isIndexDrag(event)) return;
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragOver(false);
      }}
      onDrop={clearDrag}
    >
      <EditorContent editor={editor} className="mention-editor" />
      {dragOver ? (
        <div className="mention-drop-hint" aria-hidden="true">
          <span>{t("revitIndex.dropToInsert")}</span>
        </div>
      ) : null}
    </div>
  );
});
